import React, { useState, useCallback, useMemo, type ReactNode } from 'react';
import { styled } from '@compiled/react';
import Button from '@atlaskit/button';
import { Text } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';
import { JiraInlineDialog as InlineDialog } from '@atlassian/jira-inline-dialog/src/ui/jira-inline-dialog.tsx';
import { expVal } from '@atlassian/jira-feature-experiments';
import { FormattedMessage } from '@atlassian/jira-intl';
import { useIntlV2 as useIntl } from '@atlassian/jira-intl/src/v2/use-intl.tsx';
import { useAnalyticsEvents, fireUIAnalytics } from '@atlassian/jira-product-analytics-bridge';
import { learnMoreLinkUrl } from './constants.tsx';
import messages from './messages.tsx';

export const WhatsThis = () => {
	const { formatMessage } = useIntl();
	const [isOpen, setIsOpen] = useState(false);
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const toggleDialog = useCallback(() => {
		if (!isOpen) {
			fireUIAnalytics(createAnalyticsEvent({}), 'gicRequestTypeHelpDialogButton clicked');
		}
		setIsOpen(!isOpen);
	}, [createAnalyticsEvent, isOpen]);

	const dialogContent = useMemo(() => {
		const onClickLearnMoreLink = () =>
			fireUIAnalytics(createAnalyticsEvent({}), 'gicRequestTypeLearnMoreLink clicked');

		return (
			<DialogContainer data-testid="issue-create.ui.modal.create-form.type-picker.request-type-changeboarding.dialog-container">
				<Text as="p">
					<FormattedMessage
						{...(expVal('issue-terminology-refresh-m2-replace', 'isEnabled', false)
							? messages.whatsThisDialogBodyIssueTermRefresh
							: messages.whatsThisDialogBody)}
						values={{
							learnMoreAboutRequestTypesLink: (
								<a
									href={learnMoreLinkUrl}
									target="_blank"
									onClick={onClickLearnMoreLink}
									onAuxClick={onClickLearnMoreLink}
								>
									{formatMessage(messages.learnMoreAboutRequestTypesLink)}
								</a>
							),
						}}
					/>
				</Text>
			</DialogContainer>
		);
	}, [createAnalyticsEvent, formatMessage]);

	return (
		<InlineDialog
			messageId="issue-create.ui.modal.create-form.type-picker.request-type-changeboarding.inline-dialog.jira-inline-dialog"
			messageType="engagement"
			content={dialogContent}
			isOpen={isOpen}
			placement="bottom-end"
			onClose={toggleDialog}
		>
			<WhatsThisButtonWrapper>
				<Button appearance="subtle-link" spacing="none" onClick={toggleDialog}>
					{formatMessage(messages.whatsThisDialogTrigger)}
				</Button>
			</WhatsThisButtonWrapper>
		</InlineDialog>
	);
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const WhatsThisButtonWrapper = styled.div<{
	children?: ReactNode;
}>({
	margin: 0,
	display: 'flex',
	flexDirection: 'row',
	font: token('font.body.small'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const DialogContainer = styled.div<{
	children?: ReactNode;
}>({
	width: '280px',
});
